import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Collapse,
  useTheme,
  Popover,
  Paper,
  MenuList,
  MenuItem,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { 
  FaHome, FaListAlt, FaTachometerAlt, FaQuestionCircle, 
  FaSearch, FaChartLine, FaChartBar, FaTasks, FaComment, 
  FaInfoCircle, FaChartPie, FaPlusCircle, FaClipboardList, 
  FaBars, FaRobot, FaChevronDown, FaChevronUp, FaPalette, FaLightbulb
} from 'react-icons/fa';
import FeedIcon from '@mui/icons-material/Feed';
import HistoryIcon from '@mui/icons-material/History';
import TaskIcon from '@mui/icons-material/Task';
import Psychology from '@mui/icons-material/Psychology';

import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import MenuIcon from '@mui/icons-material/Menu';
import { useSelector } from 'react-redux';
import PeopleIcon from '@mui/icons-material/People';
import { useIntl } from 'react-intl';
import { SyncAlt, Public as GlobalIcon, Explore, CalendarToday } from '@mui/icons-material';

const drawerWidth = 240;
const collapsedWidth = 60;

const DrawerStyled = muiStyled(Drawer)(({ theme, collapsed }) => ({
  '& .MuiDrawer-paper': {
    width: collapsed ? `${collapsedWidth}px` : `${drawerWidth}px`,
    transition: 'width 0.3s ease, background-color 0.3s ease',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
    color: theme.palette.mode === 'dark' ? 'white' : '#333333',
    overflow: 'auto !important',
    msOverflowStyle: 'none !important',
    scrollbarWidth: 'none !important',
    
    '@media screen and (max-width: 768px)': {
      display: 'none',
    },

    '&::-webkit-scrollbar': {
      display: 'none !important',
    },
  },
}));

const StyledListItem = muiStyled(ListItem)(({ theme, collapsed }) => ({
  color: theme.palette.mode === 'dark' ? 'white' : '#333333',
  paddingLeft: collapsed ? '16px' : '24px',

  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(25, 118, 210, 0.12)',
  },

  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
  },

  '& .MuiListItemIcon-root': {
    color: theme.palette.mode === 'dark' ? 'white' : '#333333',
    minWidth: collapsed ? 'auto' : '56px',
    justifyContent: 'center',
  },

  '& .MuiListItemText-root': {
    display: collapsed ? 'none' : 'block',
    color: theme.palette.mode === 'dark' ? 'white' : '#333333',
  },
}));

const NestedListItem = muiStyled(ListItem)(({ theme, collapsed }) => ({
  color: theme.palette.mode === 'dark' ? 'white' : '#333333',
  paddingLeft: collapsed ? '16px' : '32px',

  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(25, 118, 210, 0.12)',
  },

  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
  },

  '& .MuiListItemIcon-root': {
    color: theme.palette.mode === 'dark' ? 'white' : '#333333',
    minWidth: collapsed ? 'auto' : '40px',
    justifyContent: 'center',
  },

  '& .MuiListItemText-root': {
    display: collapsed ? 'none' : 'block',
    color: theme.palette.mode === 'dark' ? 'white' : '#333333',
  },
}));

const IconWrapper = muiStyled('div')(({ theme }) => ({
  fontSize: '24px',
  color: theme.palette.mode === 'dark' ? 'white' : '#333333',
}));

const StyledDivider = muiStyled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
}));

const ChevronIcon = muiStyled('div')(({ theme, collapsed }) => ({
  marginLeft: 'auto',
  color: theme.palette.mode === 'dark' ? 'white' : '#333333',
  display: collapsed ? 'none' : 'flex',
}));

const DrawerHeader = muiStyled('div')(({ theme }) => ({
  display: 'flex',
  marginTop: '5rem',
  alignItems: 'center',
  padding: '0 8px',
  minHeight: '64px',
  justifyContent: 'flex-end',
}));

const Sidebarnew = ({ open, onClose, collapsed, setCollapsed }) => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const intl = useIntl();
  const theme = useTheme();
  const [createOpen, setCreateOpen] = useState(false);
  const [globeOpen, setGlobeOpen] = useState(false);
  
  // States for popover dropdowns when collapsed
  const [createAnchorEl, setCreateAnchorEl] = useState(null);
  const [globeAnchorEl, setGlobeAnchorEl] = useState(null);
  
  const handleCreateClick = (event) => {
    if (collapsed) {
      setCreateAnchorEl(event.currentTarget);
    } else {
      setCreateOpen(!createOpen);
    }
  };
  
  const handleGlobeClick = (event) => {
    if (collapsed) {
      setGlobeAnchorEl(event.currentTarget);
    } else {
      setGlobeOpen(!globeOpen);
    }
  };

  const handleCreateClose = () => {
    setCreateAnchorEl(null);
  };

  const handleGlobeClose = () => {
    setGlobeAnchorEl(null);
  };

  const menuItems = [
    { 
      text: intl.formatMessage({ id: 'home' }), 
      icon: <Tooltip title="Home" arrow><span><FaHome /></span></Tooltip>, 
      path: '/' 
    },
    ...(user.data[0].type === 'Candidate'
      ? [
          { 
            text: intl.formatMessage({ id: 'dashboard' }), 
            icon: <Tooltip title="Dashboard" arrow><span><FaTachometerAlt /></span></Tooltip>, 
            path: '/candidate-dashboard' 
          },
          { 
            text: intl.formatMessage({ id: 'manageProfile' }), 
            icon: <Tooltip title="Manage Profile" arrow><span><FaInfoCircle /></span></Tooltip>, 
            path: '/Updateyourprofile' 
          },
          { 
              text: intl.formatMessage({ id: 'podActivity' }),
            icon: <Tooltip title="Pod Activity" arrow><span><FeedIcon /></span></Tooltip>, 
            path: '/Podactivitytalent' 
          },
          { 
             text: intl.formatMessage({ id: 'podRetro' }),
            icon: <Tooltip title="Pod Retro" arrow><span><HistoryIcon /></span></Tooltip>, 
            path: '/PodactivitytalentRetro' 
          },
           { 
            text: intl.formatMessage({ id: 'podTasks' }),
            icon: <Tooltip title="Pod Tasks" arrow><span><TaskIcon /></span></Tooltip>, 
            path: '/Createtasktalent' 
          },
          { 
            text: intl.formatMessage({ id: 'myPods' }), 
            icon: <Tooltip title="My Pods" arrow><span><PeopleIcon /></span></Tooltip>, 
            path: '/Pods' 
          },
          { 
            text: intl.formatMessage({ id: 'podBoard' }), 
            icon: <Tooltip title="Pod Board" arrow><span><FaTachometerAlt /></span></Tooltip>, 
            path: '/KanbanboardTalent' 
          },
          { 
            text: intl.formatMessage({ id: 'podMetrics' }), 
            icon: <Tooltip title="Pod Metrics" arrow><span><FaChartBar /></span></Tooltip>, 
            path: '/PodMetricsTalentDetailview' 
          },
          { 
            text: intl.formatMessage({ id: 'podAssist' }), 
            icon: <Tooltip title="Pod Assistant" arrow><span><FaQuestionCircle /></span></Tooltip>, 
            path: '/OpenAi' 
          },
          
          //  { 
          //   text: intl.formatMessage({ id: 'Ai Sprint Retro' }), 
          //   icon: <Tooltip title="Ai Sprint Retro" arrow><span><Psychology /></span></Tooltip>, 
          //   path: '/AiSprintRetro' 
          // },
          //  { 
          //   text:"Find Tickets", 
          //   icon: <Tooltip title="TalentTicketSearch" arrow><span><ConfirmationNumberIcon /></span></Tooltip>, 
          //   path: '/TalentTicketSearch' 
          // },
          
          
          { 
            text: intl.formatMessage({ id: 'podFeedback' }), 
            icon: <Tooltip title="Pod Feedback" arrow><span><FaComment /></span></Tooltip>, 
            path: '/Reviewsandratingcandidate' 
          },
          { 
            text: intl.formatMessage({ id: 'podsync' }), 
            icon: <Tooltip title="Pod Sync" arrow><span><SyncAlt /></span></Tooltip>, 
            path: '/MeetingSummariesTableTalent' 
          },
         
        ]
      : []),
    ...(user.data[0].type === 'Client'
      ? [
          { 
            text: intl.formatMessage({ id: 'dashboard' }), 
            icon: <Tooltip title="Dashboard" arrow><span><FaTachometerAlt /></span></Tooltip>, 
            path: '/client-dashboard' 
          },
          { 
            text: intl.formatMessage({ id: 'manageProfile' }), 
            icon: <Tooltip title="Manage Profile" arrow><span><FaInfoCircle /></span></Tooltip>, 
            path: '/client-profile' 
          },
          { 
            text: intl.formatMessage({ id: 'myPods' }), 
            icon: <Tooltip title="My Pods" arrow><span><PeopleIcon /></span></Tooltip>, 
            path: '/Team' 
          },
          { 
            text: intl.formatMessage({ id: 'createHub' }), 
            icon: <Tooltip title="Create Hub" arrow><span><FaPlusCircle /></span></Tooltip>, 
            path: '#',
            dropdown: true,
            onClick: handleCreateClick,
            open: createOpen,
            anchorEl: createAnchorEl,
            onClose: handleCreateClose,
            items: [
              {
                text: intl.formatMessage({ id: 'createSolution' }),
                icon: <Tooltip title="Create Solution" arrow><span><FaLightbulb /></span></Tooltip>,
                path: '/CreateSolution'
              },
              {
                text: intl.formatMessage({ id: 'createArt' }),
                icon: <Tooltip title="Create Art" arrow><span><FaPalette /></span></Tooltip>,
                path: '/project-requirment'
              },
              {
                text: intl.formatMessage({ id: 'createPod' }),
                icon: <Tooltip title="Create Pod" arrow><span><PeopleIcon /></span></Tooltip>,
                path: '/client-createpod'
              },
             
            ]
          },
          
          { 
            text: intl.formatMessage({ id: 'podActivity' }), 
            icon: <Tooltip title="Pod Activity" arrow><span><FaListAlt /></span></Tooltip>, 
            path: '/PodActivity' 
          },
       
          { 
            text: intl.formatMessage({ id: 'podTasks' }), 
            icon: <Tooltip title="Pod Tasks" arrow><span><FaTasks /></span></Tooltip>, 
            path: '/ViewPodTasksManagerbyid' 
          },
          { 
            text: intl.formatMessage({ id: 'POD Productivity' }), 
            icon: <Tooltip title="Pod Productivity" arrow><span><RocketLaunchIcon /></span></Tooltip>, 
            path: '/PodProductivity' 
          },
          { 
            text: intl.formatMessage({ id: 'podBoard' }), 
            icon: <Tooltip title="Pod Board" arrow><span><FaTachometerAlt /></span></Tooltip>, 
            path: '/KanbanBoard' 
          },
          { 
            text: intl.formatMessage({ id: 'podRetro' }), 
            icon: <Tooltip title="Pod Retro" arrow><span><FaClipboardList /></span></Tooltip>, 
            path: '/Podretromanager' 
          },
        
          { 
            text: intl.formatMessage({ id: 'podMetrics' }), 
            icon: <Tooltip title="Pod Metrics" arrow><span><FaChartBar /></span></Tooltip>, 
            path: '/PodMetricsmanager' 
          },
          { 
            text: intl.formatMessage({ id: 'podAssist' }), 
            icon: <Tooltip title="Pod Assistant" arrow><span><FaQuestionCircle /></span></Tooltip>, 
            path: '/OpenAi' 
          },
          { 
            text: intl.formatMessage({ id: 'podQuery' }), 
            icon: <Tooltip title="Pod Query" arrow><span><FaChartPie /></span></Tooltip>, 
            path: '/PodInsights' 
          },
          
          { 
            text: intl.formatMessage({ id: 'podAutomation' }), 
            icon: <Tooltip title="Pod Automation" arrow><span><FaRobot /></span></Tooltip>, 
            path: '/PodAutomation' 
          },
          //  { 
          //   text: intl.formatMessage({ id: 'Ai Sprint Retro' }), 
          //   icon: <Tooltip title="Ai Sprint Retro" arrow><span><Psychology /></span></Tooltip>, 
          //   path: '/AiSprintRetro' 
          // },
          { 
            text: intl.formatMessage({ id: 'podsync' }), 
            icon: <Tooltip title="Pod Sync" arrow><span><SyncAlt /></span></Tooltip>, 
            path: '/MeetingSummariesTimeline' 
          },
            
          //  { 
          //   text: intl.formatMessage({ id: 'talentexplorer' }), 
          //   icon: <Tooltip title="Talent Explorer" arrow><span>< Explore/></span></Tooltip>, 
          //   path: '/TalentMarketplace' 
          // },
      //       ...(user.data[0].emp_id === 1628  ? [
      //   {
      //     text: 'Global Insights',
      //     icon: <Tooltip title="Global Insights" arrow><span><GlobalIcon /></span></Tooltip>,
      //     path: '#',
      //     dropdown: true,
      //     onClick: handleGlobeClick,
      //     open: globeOpen,
      //     anchorEl: globeAnchorEl,
      //     onClose: handleGlobeClose,
      //     items: [
      //       {
      //         text: 'Global Lens',
      //         path: '/GlobalLens'
      //       },
      //       {
      //         text: 'Global Rollouts',
      //         path: '/GlobalRollouts'
      //       },
      //       {
      //         text: 'Global VOC',
      //         path: '/GlobalVOC'
      //       },
      //        {
      //         text: 'Talent Explorer',
      //         path: '/TalentMarketplace'
      //       }
      //     ]
      //   }
      // ] : []),
        
          ...(user.data[0].manager_type === 'PartnerClient' 
            ? [
              ...(user.data[0].emp_id === 1641  ? [
              { 
                text: intl.formatMessage({ id: 'partnerMetrics' }), 
                icon: <Tooltip title="Partner Metrics" arrow><span><FaChartLine /></span></Tooltip>, 
                path: '/PartnerMetricsDashboard' 
              }]:[])]
            : []
          ),
        ]
      : []),
    ...(user.data[0].type === 'stakeholder'
      ? [
          { 
            text: intl.formatMessage({ id: 'dashboard' }), 
            icon: <Tooltip title="Dashboard" arrow><span><FaTachometerAlt /></span></Tooltip>, 
            path: '/StakeholderDashboard' 
          },
          { 
            text: intl.formatMessage({ id: 'podActivity' }), 
            icon: <Tooltip title="Pod Activity" arrow><span><FaListAlt /></span></Tooltip>, 
            path: '/PodActivitystakeholderviewall' 
          },
          { 
            text: intl.formatMessage({ id: 'podMetrics' }), 
            icon: <Tooltip title="Pod Metrics" arrow><span><FaChartBar /></span></Tooltip>, 
            path: '/PodMetricsmanager' 
          },
          { 
            text: intl.formatMessage({ id: 'manageProfile' }), 
            icon: <Tooltip title="Manage Profile" arrow><span><FaInfoCircle /></span></Tooltip>, 
            path: '/stakeholder-profile' 
          },
        ]
      : []),
  ];

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
    if (!collapsed) {
      setCreateOpen(false);
      setGlobeOpen(false);
    }
  };

  const renderDropdownPopover = (item) => {
    if (!item.dropdown || !collapsed) return null;

    return (
      <Popover
        open={Boolean(item.anchorEl)}
        anchorEl={item.anchorEl}
        onClose={item.onClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        sx={{ ml: 1 }}
      >
        <Paper sx={{ 
          backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff', 
          color: theme.palette.mode === 'dark' ? 'white' : '#333333' 
        }}>
          <MenuList>
            {item.items.map((subItem) => (
              <MenuItem
                key={subItem.text}
                component={Link}
                to={subItem.path}
                onClick={item.onClose}
                sx={{
                  color: theme.palette.mode === 'dark' ? 'white' : '#333333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(25, 118, 210, 0.12)',
                  }
                }}
              >
                {subItem.icon && (
                  <ListItemIcon sx={{ 
                    color: theme.palette.mode === 'dark' ? 'white' : '#333333', 
                    minWidth: '40px' 
                  }}>
                    {subItem.icon}
                  </ListItemIcon>
                )}
                <ListItemText primary={subItem.text} />
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popover>
    );
  };

  return (
    <>
      <DrawerStyled 
        variant="persistent" 
        anchor="left" 
        open={open} 
        collapsed={collapsed}
      >
        <DrawerHeader>
          <IconButton onClick={handleToggleCollapse}>
            <MenuIcon sx={{ 
              color: theme.palette.mode === 'dark' ? '#fff' : '#333333' 
            }} />
          </IconButton>
        </DrawerHeader>
        <StyledDivider />
        <List>
          {menuItems.map((item) => (
            item.dropdown ? (
              <React.Fragment key={item.text}>
                <StyledListItem
                  button
                  onClick={item.onClick}
                  selected={location.pathname === item.path}
                  collapsed={collapsed}
                >
                  <ListItemIcon collapsed={collapsed}>
                    <IconWrapper>{item.icon}</IconWrapper>
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  <ChevronIcon collapsed={collapsed}>
                    {item.open ? <FaChevronUp /> : <FaChevronDown />}
                  </ChevronIcon>
                </StyledListItem>
                
                {/* Regular dropdown for expanded sidebar */}
                <Collapse in={item.open && !collapsed} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.items.map((subItem) => (
                      <Tooltip
                        key={subItem.text}
                        title={collapsed ? subItem.text : ""}
                        placement="right"
                        arrow
                      >
                        <NestedListItem
                          button
                          component={Link}
                          to={subItem.path}
                          selected={location.pathname === subItem.path}
                          collapsed={collapsed}
                        >
                          <ListItemIcon collapsed={collapsed}>
                            <IconWrapper>{subItem.icon}</IconWrapper>
                          </ListItemIcon>
                          <ListItemText primary={subItem.text} />
                        </NestedListItem>
                      </Tooltip>
                    ))}
                  </List>
                </Collapse>
                
                {/* Popover dropdown for collapsed sidebar */}
                {renderDropdownPopover(item)}
              </React.Fragment>
            ) : (
              <Tooltip
                key={item.text}
                title={collapsed ? item.text : ""}
                placement="right"
                arrow
              >
                <StyledListItem
                  button
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                  collapsed={collapsed}
                >
                  <ListItemIcon collapsed={collapsed}>
                    <IconWrapper>{item.icon}</IconWrapper>
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </StyledListItem>
              </Tooltip>
            )
          ))}
        </List>
      </DrawerStyled>
    </>
  );
};

export default Sidebarnew;